import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';

import { Radio, FormControlLabel } from '@material-ui/core';
import NgpRadioGroup from '../../form/NgpRadioGroup';
import EnableDisableBox from '../../form/EnableDisableBox';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import RuleForm from './RuleForm';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS, MANUAL_ERROR_TYPE } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFormQueryService, useFind } from '../../../services/FormQueryService';
import ListFormField from '../../form/ListFormField';
import { throwValidationError, useDataService } from '../../../services/ApiDataService';

import { FIELD_MAPPINGS, AccessPointRulesApi, PollingApi } from './PollingConstants';

import { usePollingStyles } from './PollingStyles';

const getColumns = (fieldName) => {
    const columns = {
        [FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS]: [
            {
                type: COLUMN_TYPES.TEXT,
                sortable: true,
                mapping: FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS,
                flex: 1,
            },
        ],
    };
    return columns[fieldName] || [];
};

function prepareSavedData(dataToSave) {
    const ruleField = FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS;
    if (_.isEmpty(dataToSave[ruleField])) {
        return {
            ...dataToSave,
            [ruleField]: [],
        };
    }
    return {
        ...dataToSave,
        [ruleField]: _.map(dataToSave[ruleField], (rule) => rule[ruleField]),
    };
}

const defaultFormValues = {
    [FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS]: [],
    [FIELD_MAPPINGS.ANY_ZONE_INCLUDED]: true,
};

const AccessPointSelectionTab = forwardRef((props, ref) => {
    const [deviceEnabled, setDeviceEnabled] = useState(true);
    const classes = usePollingStyles();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });

    useImperativeHandle(ref, () => ({
        save: async () => {
            return methods.handleSubmit(handleFormSubmit)();
        },
    }));
    const [configIsDirty, setConfigIsDirty] = useState(false);
    const { reset, setError, watch } = methods;
    const anyZoneIncluded = watch(FIELD_MAPPINGS.ANY_ZONE_INCLUDED);
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const formQueryService = useFormQueryService();

    useEffect(() => {
        formQueryService.queryAPI(PollingApi).then((pollingConfig) => {
            setDeviceEnabled(_.get(pollingConfig, FIELD_MAPPINGS.DEVICE_ENABLED, false));
        });
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const postFindHandler = (response) => {
        const _response = response[0] || _.first(response) || response;
        const cleaned = _.cloneDeep(_response) || {};
        cleaned[FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS] = _.map(
            cleaned[FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS],
            (pattern) => ({
                [FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS]: pattern,
            })
        );

        return cleaned;
    };

    const { initialized, finding, formData, resetForm } = useFind({
        url: AccessPointRulesApi,
        postFindHandler,
        resetFunc: reset,
        defaultValues: defaultFormValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        if (!dataToSave.anyZoneIncluded && _.isEmpty(dataToSave.includedZonePatterns)) {
            setError(FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS, {
                type: MANUAL_ERROR_TYPE,
                message: 'At least one string is required',
            });
            return throwValidationError();
        }
        const putData = prepareSavedData(dataToSave);

        return formSaveService.save({
            url: AccessPointRulesApi,
            saveFunc: () =>
                dataService.put(AccessPointRulesApi, putData).then(() => {
                    setConfigIsDirty(false);
                    resetForm();
                }),
            dataToSave: putData,
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const dirtyCheck = (isDirty) => isDirty || configIsDirty;

    useImperativeHandle(ref, () => ({
        save: methods.handleSubmit(handleSubmitFunction),
    }));

    if (initialized && !deviceEnabled) {
        return (
            <div className={classes.interfaceRuleMessage}>
                {t`Please be noted 'Monitor network devices' is disabled in Polling setting!`}
            </div>
        );
    }

    return (
        <FormProvider {...methods}>
            <NgpForm
                submitFunction={handleSubmitFunction}
                initialized={initialized}
                loading={finding || saving}
                isDirtyFunc={dirtyCheck}
                showRequiredFieldsTip={false}
            >
                <FormSection
                    title={<Trans>Ruckus® Virtual SmartZone Access Point Zone Inclusion</Trans>}
                    subText={
                        <Trans>
                            This section allows you to configure nGeniusPULSE to only monitor Ruckus® APs belonging to
                            zones containing the names below. All names are case-insensitive.
                        </Trans>
                    }
                >
                    <NgpRadioGroup fieldName={FIELD_MAPPINGS.ANY_ZONE_INCLUDED}>
                        <FormControlLabel value={'true'} label={t`Monitor all access points`} control={<Radio />} />
                        <FormControlLabel
                            value={'false'}
                            label={t`Monitor access points belonging to zones with the following strings in their name:`}
                            control={<Radio />}
                        />
                    </NgpRadioGroup>
                    <EnableDisableBox disabled={anyZoneIncluded}>
                        <ListFormField
                            fieldName={FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS}
                            dbSelected={_.get(formData, FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS)}
                            addButtonText={t`ADD STRING`}
                            gridColumns={getColumns(FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS)}
                            formComponent={
                                <RuleForm
                                    fieldName={FIELD_MAPPINGS.INCLUDED_ZONE_PATTERNS}
                                    fieldLabel={t`Zone Name Includes`}
                                    title={t`Ruckus® Zone Inclusion Rule`}
                                    addButtonText={t`Save`}
                                    updateButtonText={t`Save`}
                                    uniqueErrorText={t`Value must be unique`}
                                    contentStyle={{}}
                                />
                            }
                            setSelectionIsDirty={setConfigIsDirty}
                            showHeaders={false}
                            emptyGridText={t`No access point inclusion rules`}
                        />
                    </EnableDisableBox>
                </FormSection>
            </NgpForm>
        </FormProvider>
    );
});

export default AccessPointSelectionTab;
